// src/belediye/components/mesajlasma/TaskComposer.js
// FAZ-42: Sohbet içi görev verme — WizardQuestion adım-adım akış.
//
// FAZ-83 (kullanıcı kararları, 2026-08-01) — ADIMLAR BİRLEŞTİ. Eskiden her
// soru ayrı bir ekrandı; detaylı talep 8-9 adım sürüyordu. Üç birleştirme:
//   1) Başlık + Açıklama  → tek kutu + seçim çipi   (A2 → BaslikAciklamaAdimi)
//   2) Mahalle+Konum+Adres → üç sekme               (Y2 → YerAdimi)
//   3) Aciliyet + Süre     → iki çip satırı         (S1 → AciliyetSureAdimi)
// Yeni akış:
//   • "hizli"   → Açıklama → Aciliyet+Süre → Ek                     (3 adım)
//   • "detayli" → Başlık+Açıklama → Yer → Aciliyet+Süre →
//                 (Sorumlu) → Ek                                  (4-5 adım)
// Parantezli adım KOŞULLU: "Sorumlu" yalnız birden çok kişi seçildiğinde.
// Hiçbir adım zorunlu değil — hepsinde "Atla" var (FAZ-83/B1: SOL uçta).
// createTask payload sözleşmesi ve onDone(task) KORUNUR.
//
// HIZLI TALEPTE BAŞLIK YOK (kullanıcı: "hızlı talepte başlık olmayacak,
// şu anki gibi") — başlık açıklamanın ilk satırından türetilir.
import React, { useCallback, useMemo, useState } from "react";
import { DocumentIcon, PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import { createTask, uploadAttachment } from "../../services/internalTasksApi";
import WizardQuestion from "./WizardQuestion";
// FAZ-83: birleşik adımlar
import AciliyetSureAdimi from "./AciliyetSureAdimi";
import BaslikAciklamaAdimi from "./BaslikAciklamaAdimi";
import SihirbazAltSatir from "./SihirbazAltSatir";
import YerAdimi from "./YerAdimi";

// FAZ-55/10 — EK ADIMI, diğer adımlarla AYNI dilde: araç üstte (dosya seçme
// alanı), altta TEK mesaj kutusu satırı (Atla + Gönder). Seçilen dosyaların
// önizlemesi sihirbazın üstündeki `.msj-staged` şeridinde zaten duruyor.
//
// FAZ-74 — DOSYA PENCERESİNİ ARTIK JAVASCRIPT AÇMIYOR.
// Eskiden bu alan bir <button> idi ve `onSec` içinde gizli girişin
// `.click()`'i çağrılıyordu. Tarayıcı, dosya seçicisini yalnız geçerli bir
// "kullanıcı etkinliği" penceresinde açar; açmadığında çağrıyı SESSİZCE
// yutar — ne pencere, ne hata, ne istek. Kanıt (2026-07-29 nginx kaydı):
// kullanıcının tarayıcısından BUGÜNE KADAR TEK BİR ek yükleme isteği
// çıkmamış; buna karşılık 19:17:20'de eksiz bir görev 201 ile oluşmuş.
// Yani dosya hiç seçilemiyordu ve "Bu dosyalarla devam et" (adet=0 iken
// pasif) yüzünden sihirbaz İLERLEYEMİYORDU → "fotoğraf eklenince görevi bile
// oluşturmuyor" şikâyeti birebir buydu.
// Artık alan bir <label> ve dosya girişi ONUN İÇİNDE: pencereyi tarayıcının
// kendisi açar (yerel etiket→giriş etkinleştirmesi), araya JS girmez.
// `onDosya` bir change handler'dır (eski `onSec` tetikleyicisinin yerine).
export function EkSecici({ adet = 0, onDosya, onDone, onSkip }) {
  return (
    <div className="msj-ek">
      <label className="msj-ek-alan">
        {/* FAZ-78: mobilde `hidden` giriş dosya seçiciyi açmıyor — bkz. .msj-gizli-dosya */}
        <input type="file" className="msj-gizli-dosya" multiple onChange={onDosya} />
        <PaperClipIcon />
        <span>
          {adet > 0 ? "Başka dosya ekle" : "Fotoğraf veya belge ekle"}
          <small>Sorumlu işe başlarken bunları görür</small>
        </span>
      </label>
      {/* FAZ-83/B1: Atla SOL uçta, Gönder SAĞ uçta — ortak alt satırdan. */}
      <SihirbazAltSatir
        sinif="msj-due-onay"
        onSkip={onSkip}
        skipTitle="Eklemeden devam et"
        ozet={
          adet > 0 ? (
            <>
              <b>{adet} dosya</b> gönderilecek
            </>
          ) : (
            "Dosya seçilmedi"
          )
        }
        gonderAktif={adet > 0}
        onGonder={onDone}
        gonderTitle="Bu dosyalarla devam et"
      />
    </div>
  );
}

export default function TaskComposer({
  conversationId,
  targets = [],
  unitTarget = null,
  mode = "hizli",
  onDone,
  onCancel,
}) {
  const [submitting, setSubmitting] = useState(false);
  const detayli = mode === "detayli" || !!unitTarget;

  // FAZ-55/3 — GÖREV AÇILIRKEN EK. Kroki, yer fotoğrafı, teknik belge… görevli
  // işe başlarken neye baktığını görsün. Görev ÖNCE oluşur, ekler sonra
  // yüklenir (uç zaten task id ister); yükleme hatası görevi geçersiz kılmaz.
  const [ekler, setEkler] = useState([]); // [{id,file,url,isImage}]

  // FAZ-74: artık gizli bir girişin tetikleyicisi değil, <label> içindeki
  // girişin KENDİ change handler'ı (bkz. EkSecici başındaki not). `useCallback`
  // + boş bağımlılık: yalnız `setEkler`in fonksiyonel biçimini kullanıyor,
  // adım listesini (useMemo) her render'da yeniden kurmasın.
  const dosyaSec = useCallback((e) => {
    const secilenler = [...(e.target.files || [])];
    e.target.value = ""; // aynı dosya tekrar seçilebilsin
    if (!secilenler.length) return;
    setEkler((prev) => [
      ...prev,
      ...secilenler.map((f, i) => ({
        id: `${Date.now()}-${prev.length + i}-${f.name}`,
        file: f,
        url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
        isImage: f.type.startsWith("image/"),
      })),
    ]);
  }, []);
  const ekKaldir = (id) =>
    setEkler((prev) => {
      const cikan = prev.find((x) => x.id === id);
      if (cikan?.url) URL.revokeObjectURL(cikan.url);
      return prev.filter((x) => x.id !== id);
    });

  const steps = useMemo(() => {
    // HIZLI akışın açıklaması — başlık sorulmadığı için tek kutulu klasik
    // metin adımı olarak kalır (kullanıcı: "hızlı talepte başlık olmayacak").
    const descStep = {
      key: "description",
      kind: "text",
      multiline: true,
      optional: true,
      title: "Ne yapılacak?",
      placeholder: "Kısaca ne yapılacak?",
      maxLength: 2000,
    };
    // FAZ-83/A2 — BAŞLIK + AÇIKLAMA tek adımda (yalnız detaylı akış).
    const neStep = {
      key: "ne",
      kind: "picker",
      title: "Ne yapılacak?",
      render: ({ onDone, onSkip, acc }) => (
        <BaslikAciklamaAdimi
          initialTitle={acc.title || ""}
          initialDescription={acc.description || ""}
          onDone={onDone}
          onSkip={onSkip}
        />
      ),
    };
    // FAZ-83/Y2 — MAHALLE + KONUM + ADRES tek adımda, üç sekme.
    // Üçü hâlâ AYRI bilgidir (bkz. YerAdimi başlığı); birleşen yalnız ekran.
    const yerStep = {
      key: "yer",
      kind: "picker",
      title: "Yer",
      render: ({ onDone, onSkip, acc }) => (
        <YerAdimi acc={acc} onDone={onDone} onSkip={onSkip} />
      ),
    };
    // FAZ-83/S1 — ACİLİYET (üstte) + SÜRE (altta) tek adımda.
    const acilSureStep = {
      key: "acilsure",
      kind: "picker",
      title: "Aciliyet ve süre",
      render: ({ onDone, onSkip, acc }) => (
        <AciliyetSureAdimi acc={acc} onDone={onDone} onSkip={onSkip} />
      ),
    };
    // FAZ-55/3 — SORUMLU. Backend listedeki İLK kişiyi sorumlu, kalanları
    // katılımcı yapıyor (commands.py). FAZ-55/16: adım atlanabilir ve HIZLI
    // görevde hiç yok. FAZ-83: aciliyet+süre paneline KATILMADI — "ne kadar
    // acil" ile "kim yapacak" farklı sorular ve isim çipleri uzun.
    const sorumluStep = {
      key: "sorumlu",
      kind: "options",
      title: "Asıl sorumlu kim?",
      optional: true,
      options: targets.map((t) => ({
        label: t.display_name,
        hint: "Diğerleri katılımcı olur",
        patch: { sorumlu_id: t.id },
      })),
    };
    // FAZ-55/3+10+12 — EK. Adım `tasks.attachment.add` iznine BAĞLI DEĞİL;
    // izin listesi tarayıcıda eskiyebildiği için adım sessizce kaybolmasın
    // (yükleme kapısı uçta duruyor, yetkisiz yükleme görevi geçersiz kılmaz).
    const ekStep = {
      key: "ek",
      kind: "picker",
      title: "Fotoğraf veya belge",
      render: ({ onDone, onSkip }) => (
        <EkSecici
          adet={ekler.length}
          onDosya={dosyaSec}
          onDone={() => onDone({})}
          onSkip={onSkip}
        />
      ),
    };

    // Sorumlu adımı YALNIZ birden çok kişi seçiliyken anlamlı (tek kişide zaten o).
    const cokKisi = targets.length > 1 && !unitTarget;

    if (detayli) {
      return [
        neStep,
        yerStep,
        acilSureStep,
        ...(cokKisi ? [sorumluStep] : []),
        // EK en sonda: görev tarif edildikten sonra "istersen bir de fotoğraf"
        // doğal sıra; başta sorulduğunda akışı kesiyordu.
        ekStep,
      ];
    }
    // HIZLI: açıklama → aciliyet+süre → fotoğraf (kullanıcı, 2026-08-01:
    // "açıklamadan sonra aciliyet ve süre kısımları da seçilmeli, belki yeni
    // çip şeklinde, sonra fotoğraf seçme"). Sorumlu adımı hızlıda yok.
    return [descStep, acilSureStep, ekStep];
  }, [detayli, targets, unitTarget, ekler.length, dosyaSec]);

  const onComplete = async (ans) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const title =
        (ans.title || "").trim() ||
        (ans.description || "").trim().split("\n")[0].slice(0, 80) ||
        (detayli ? "Talep" : "Hızlı talep");
      // FAZ-55/3: seçilen sorumlu listenin BAŞINA alınır — backend ilk kişiyi
      // sorumlu, kalanları katılımcı yapar. Seçim yoksa eski davranış (ilk kişi).
      let hedefIdleri = targets.map((t) => t.id);
      if (ans.sorumlu_id) {
        hedefIdleri = [
          ans.sorumlu_id,
          ...hedefIdleri.filter((id) => id !== ans.sorumlu_id),
        ];
      }
      const payload = {
        title,
        assignee_user_ids: hedefIdleri,
        priority: ans.priority || "normal",
        source_conversation_id: conversationId,
      };
      if (unitTarget) payload.assignee_unit_ids = [unitTarget.id];
      if (ans.due_date) payload.due_date = ans.due_date;
      if (ans.due_date && ans.due_time) payload.due_time = ans.due_time;
      if ((ans.description || "").trim()) payload.description = ans.description.trim();
      // FAZ-55/8: yer bilgisi (yalnız detaylı akış). Üçü de bağımsız ve isteğe
      // bağlı — atlanan hiç gönderilmez, boş değer yazılmaz.
      // `neighborhood_names` yalnız sihirbaz özeti içindir, payload'a GİRMEZ
      // (backend id bekler); mahalle artık ÇOKLU (neighborhood_ids).
      if (ans.neighborhood_ids?.length) payload.neighborhood_ids = ans.neighborhood_ids;
      if ((ans.address || "").trim()) payload.address = ans.address.trim();
      if (ans.latitude != null && ans.longitude != null) {
        payload.latitude = ans.latitude;
        payload.longitude = ans.longitude;
      }
      const task = await createTask(payload);

      // FAZ-55/3: ekler görev doğduktan SONRA yüklenir (uç task id ister).
      // Yükleme hatası GÖREVİ geçersiz kılmaz — görev duruyor, kullanıcıya
      // hangi dosyanın yüklenemediği söylenir, detaydan tekrar denenebilir.
      if (ekler.length > 0) {
        const basarisiz = [];
        for (const ek of ekler) {
          try {
            await uploadAttachment(task.id, ek.file);
          } catch {
            basarisiz.push(ek.file.name);
          }
        }
        ekler.forEach((ek) => ek.url && URL.revokeObjectURL(ek.url));
        if (basarisiz.length) {
          toast.error(`Talep oluştu ama şu dosyalar yüklenemedi: ${basarisiz.join(", ")}`);
        }
      }

      toast.success("Talep oluşturuldu ve sohbete işlendi. 🎉");
      onDone && onDone(task);
    } catch (e) {
      toast.error(
        e?.response?.data?.assignees || e?.response?.data?.detail || "Talep oluşturulamadı."
      );
      setSubmitting(false);
    }
  };

  const targetNames =
    [
      ...targets.map((t) => t.display_name),
      ...(unitTarget ? [`${unitTarget.name} (birimin tümü)`] : []),
    ].join(", ") || "sohbettekiler";

  return (
    <div className="msj-tc">
      {/* FAZ-74: ortak gizli dosya girişi KALDIRILDI — giriş artık EkSecici'nin
          <label>'ının içinde, yani tıklanan öğenin kendisinde. */}
      <div className="msj-tc-kime">
        Kime: <b>{targetNames}</b>
        {/* FAZ-55/3: sorumlu artık AÇIKÇA soruluyor → "(ilki sorumlu)" varsayım
            notu yalnız soru sorulmayan durumda (birim ataması) anlamlı. */}
        {targets.length > 1 && unitTarget && " (ilki sorumlu)"}
      </div>
      {/* Seçilen ekler — küçük önizleme (sohbetteki .msj-staged deseniyle aynı) */}
      {ekler.length > 0 && (
        <div className="msj-staged">
          {ekler.map((ek) => (
            <div key={ek.id} className={`msj-staged-item ${ek.isImage ? "is-img" : "is-file"}`}>
              {ek.isImage ? (
                <img src={ek.url} alt="" className="msj-staged-thumb" />
              ) : (
                <span className="msj-staged-file">
                  <DocumentIcon />
                  <span className="msj-staged-name">{ek.file.name}</span>
                </span>
              )}
              <button
                type="button"
                className="msj-staged-x"
                onClick={() => ekKaldir(ek.id)}
                title="Kaldır"
                aria-label="Kaldır"
              >
                <XMarkIcon />
              </button>
            </div>
          ))}
        </div>
      )}
      <WizardQuestion
        steps={steps}
        submitting={submitting}
        onComplete={onComplete}
        onCancel={onCancel}
      />
    </div>
  );
}
